import React, { useState, useEffect, useCallback, useRef } from "react";
import * as SubframeCore from "@subframe/core";
import { DialogLayout, Button, TextField, DropdownMenu } from "../../../../ui";
import {
  FeatherChevronDown,
  FeatherSearch,
  FeatherCheck,
} from "@subframe/core";
import { CustomerInvoice } from "../../../../types";
import { TDSRate, fetchTDSRates } from "../../../../services/api";
import { GetInvoicesForCustomerUseCase } from "../../../../shared/domain/use-cases/GetInvoicesForCustomerUseCase";
import { InvoiceRepositoryImpl } from "../../../../shared/data/repositories/InvoiceRepositoryImpl";
import { InvoiceListItemEntity } from "../../../../shared/domain/entities/InvoiceListItemEntity";
import {
  useToast,
  ErrorBanner,
} from "../../../../shared/presentation/components";
import { useErrorHandling } from "../../../../shared/presentation/hooks";

interface ApplyCreditsModalProps {
  open: boolean;
  customerId: string;
  customerName: string;
  availableCredits: number;
  currencySymbol: string;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const ApplyCreditsModal: React.FC<ApplyCreditsModalProps> = ({
  open,
  customerId,
  customerName,
  availableCredits,
  currencySymbol,
  onOpenChange,
  onSuccess,
}) => {
  const { showSuccess, showError, showWarning } = useToast();
  const {
    errors,
    setApiErrors,
    setFieldError,
    clearErrors,
    hasErrors: checkHasErrors,
  } = useErrorHandling();

  // State for invoices
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoicesOffset, setInvoicesOffset] = useState(0);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState<string>("");

  // Initialize use case
  const getInvoicesUseCase = React.useMemo(
    () => new GetInvoicesForCustomerUseCase(new InvoiceRepositoryImpl()),
    []
  );

  // State for selected invoices with amounts
  const [selectedInvoices, setSelectedInvoices] = useState<
    Array<{
      invoice: CustomerInvoice;
      amount: number;
      tds_tax_amount: number | undefined;
      tds_tax_id: string | undefined;
      tds_section: string | undefined;
      tds_tax_percentage: number | undefined;
      tds_tax_name: string | undefined;
      is_tds_applied: boolean | undefined | number;
      isSelected: boolean;
    }>
  >([]);

  // State for TDS rates
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);

  // State for applying credits
  const [isApplying, setIsApplying] = useState(false);

  // Track if pay in full has been used
  const [payInFullUsed, setPayInFullUsed] = useState(false);

  // Search timeout ref for debouncing
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevSearchTermRef = useRef<string>("");

  // Helper function to convert InvoiceListItemEntity to CustomerInvoice
  const convertToCustomerInvoice = (
    entity: InvoiceListItemEntity
  ): CustomerInvoice => {
    return {
      id: entity.id?.toString() ?? "",
      invoice_id: entity.invoice_id,
      invoice_number: entity.invoice_number,
      date: entity.date,
      total: entity.total,
      balance: entity.balance,
      unprocessed_payment_amount: entity.unprocessed_payment_amount,
      tax_total: entity.tax_total,
      is_tds_applied: entity.is_tds_applied,
      currency_code: entity.currency_code,
      currency_symbol: entity.currency_symbol,
      tds_tax_amount: entity.tds_tax_amount,
      tds_section: entity.tds_section ?? undefined,
      tds_tax_id: entity.tds_tax_id ?? undefined,
      tds_tax_percentage: entity.tds_tax_percentage,
      tds_tax_name: entity.tds_tax_name ?? undefined,
    };
  };

  // Fetch TDS rates
  const fetchTDSRatesData = async () => {
    try {
      const response = await fetchTDSRates();
      if (response.success) {
        setTdsRates(response.data.tds_rates);
      }
    } catch (error) {
      console.error("Failed to fetch TDS rates:", error);
    }
  };

  // Fetch customer invoices
  const fetchCustomerInvoices = useCallback(
    async (reset: boolean = true) => {
      if (reset) {
        setInvoicesOffset(0);
      }

      setLoadingInvoices(true);
      setInvoicesLoading(true);
      try {
        const result = await getInvoicesUseCase.execute(
          customerId,
          5,
          reset ? 0 : invoicesOffset,
          invoiceSearchTerm || undefined
        );

        // Filter invoices with balance > 0 and convert to CustomerInvoice
        const invoicesWithBalance = result.items
          .filter((invoice) => invoice.balance > 0)
          .map(convertToCustomerInvoice);

        setInvoicesHasMore(result.pagination.has_more);
        setInvoicesOffset(result.pagination.offset + result.pagination.limit);

        // Initialize or update selectedInvoices
        const newSelectedInvoices = invoicesWithBalance.map((invoice) => ({
          invoice,
          amount: 0,
          isSelected: false,
          tds_tax_amount: invoice.tds_tax_amount,
          tds_tax_id: invoice.tds_tax_id,
          tds_section: invoice.tds_section,
          tds_tax_percentage: invoice.tds_tax_percentage,
          tds_tax_name: invoice.tds_tax_name,
          is_tds_applied: invoice.is_tds_applied,
        }));

        if (reset) {
          setSelectedInvoices(newSelectedInvoices);
        } else {
          setSelectedInvoices((prev) => [...prev, ...newSelectedInvoices]);
        }
      } catch (error) {
        console.error("Failed to fetch customer invoices:", error);
        if (reset) {
          setSelectedInvoices([]);
        }
      } finally {
        setLoadingInvoices(false);
        setInvoicesLoading(false);
      }
    },
    [getInvoicesUseCase, invoicesOffset, invoiceSearchTerm, customerId]
  );

  const loadMoreInvoices = useCallback(() => {
    if (!invoicesHasMore || invoicesLoading) return;
    fetchCustomerInvoices(false);
  }, [invoicesHasMore, invoicesLoading, fetchCustomerInvoices]);

  // Fetch TDS rates and invoices when modal opens
  useEffect(() => {
    if (open) {
      fetchTDSRatesData();
      fetchCustomerInvoices(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search effect
  useEffect(() => {
    if (!open) return;

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const currentSearchTerm = invoiceSearchTerm.trim();
    const previousSearchTerm = prevSearchTermRef.current.trim();

    // If search term is empty and it was previously non-empty (user cleared the search), refetch immediately
    if (!currentSearchTerm && previousSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      setInvoicesOffset(0);
      fetchCustomerInvoices(true);
      return;
    }

    // If search term has value, debounce the search
    if (currentSearchTerm) {
      prevSearchTermRef.current = currentSearchTerm;
      searchTimeoutRef.current = setTimeout(() => {
        setInvoicesOffset(0);
        fetchCustomerInvoices(true);
      }, 300);
    } else {
      // Update ref even when search is empty (for initial state)
      prevSearchTermRef.current = currentSearchTerm;
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSearchTerm, open]);

  // Calculate remaining credits (amount + TDS)
  // If TDS is already applied, don't count it in the credits calculation
  const calculateRemainingCredits = () => {
    const totalUsed = selectedInvoices
      .filter((item) => item.isSelected)
      .reduce((sum, item) => {
        const amount = item.amount || 0;
        // If TDS is already applied, don't add TDS to the total
        const tds = item.is_tds_applied ? 0 : item.tds_tax_amount || 0;
        return sum + amount + tds;
      }, 0);
    return Math.max(0, availableCredits - totalUsed);
  };

  const remainingCredits = calculateRemainingCredits();

  // Get maximum amount that can be entered for a specific invoice
  const getMaxAmountForInvoice = (invoiceId: string): number => {
    const currentInvoice = selectedInvoices.find(
      (item) => item.invoice.invoice_id === invoiceId
    );

    if (!currentInvoice) return 0;
    console.log("currentInvoice", currentInvoice);
    // Calculate total credits used by other selected invoices (amount + TDS)
    const otherInvoicesTotal = selectedInvoices
      .filter(
        (item) => item.isSelected && item.invoice.invoice_id !== invoiceId
      )
      .reduce((sum, item) => {
        const amount = item.amount || 0;
        // If TDS is already applied, don't add TDS to the total
        const tds = item.is_tds_applied ? 0 : item.tds_tax_amount || 0;
        return sum + amount + tds;
      }, 0);

    const availableCreditsForThis = availableCredits - otherInvoicesTotal;

    // Get invoice's remaining balance
    const invoiceBalance = currentInvoice.invoice.balance || 0;

    // Get TDS amount for this invoice
    const tdsAmount = currentInvoice.tds_tax_amount || 0;

    // Check if TDS is already applied to this invoice
    const isTdsApplied = currentInvoice.is_tds_applied ? true : false;

    // Calculate the maximum payment amount considering:
    // 1. Available credits (availableCredits - other invoices' total)
    // 2. Invoice balance constraint (paymentAmount + tdsAmount <= invoiceBalance)
    //    BUT: If TDS is already applied, don't subtract it again
    const maxFromCredits = isTdsApplied
      ? availableCreditsForThis
      : availableCreditsForThis - tdsAmount;
    const maxFromInvoice = isTdsApplied
      ? invoiceBalance
      : invoiceBalance - tdsAmount;

    return Math.max(0, Math.min(maxFromCredits, maxFromInvoice));
  };

  // Truncate text helper
  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  // Format amount helper
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleClose = () => {
    setSelectedInvoices([]);
    setInvoiceSearchTerm("");
    setPayInFullUsed(false);
    setInvoicesOffset(0);
    clearErrors();
    onOpenChange(false);
  };

  const handleInvoiceSelect = (invoiceId: string) => {
    setSelectedInvoices((prev) => {
      const updated = prev.map((item) => {
        if (item.invoice.invoice_id === invoiceId) {
          const newIsSelected = !item.isSelected;
          return {
            ...item,
            isSelected: newIsSelected,
            amount: newIsSelected ? item.amount : 0,
          };
        }
        return item;
      });

      const hasNewlySelectedWithoutAmount = updated.some(
        (item) => item.isSelected && (item.amount === 0 || !item.amount)
      );

      if (hasNewlySelectedWithoutAmount) {
        setPayInFullUsed(false);
      }

      return updated;
    });
  };

  const handleAmountChange = (invoiceId: string, amount: number) => {
    setSelectedInvoices((prev) => {
      const invoiceItem = prev.find(
        (item) => item.invoice.invoice_id === invoiceId
      );

      if (!invoiceItem) return prev;

      const roundedAmount = Math.round(amount * 100) / 100;
      const maxAllowed = getMaxAmountForInvoice(invoiceId);

      if (roundedAmount > maxAllowed + 0.001) {
        showWarning(
          `Amount cannot exceed ${currencySymbol}${formatAmount(maxAllowed)}`
        );
        return prev;
      }

      // Check if TDS is already applied to this invoice
      const isTdsApplied = !!invoiceItem.is_tds_applied;
      const tdsAmount = invoiceItem.tds_tax_amount || 0;
      const invoiceBalance = invoiceItem.invoice.balance || 0;

      // Check if payment amount + TDS exceeds invoice balance
      // BUT: If TDS is already applied, don't add TDS to the total
      const totalPayment = isTdsApplied
        ? roundedAmount
        : roundedAmount + tdsAmount;
      const maxAllowedFromInvoice = isTdsApplied
        ? invoiceBalance
        : invoiceBalance - tdsAmount;

      if (roundedAmount > maxAllowedFromInvoice + 0.001) {
        if (isTdsApplied) {
          showWarning(
            `Payment amount (${currencySymbol}${formatAmount(
              roundedAmount
            )}) cannot exceed the invoice's remaining balance of ${currencySymbol}${formatAmount(
              invoiceBalance
            )}.`
          );
        } else {
          showWarning(
            `Payment amount (${currencySymbol}${formatAmount(
              roundedAmount
            )}) + TDS (${currencySymbol}${formatAmount(
              tdsAmount
            )}) = ${currencySymbol}${formatAmount(
              totalPayment
            )} cannot exceed the invoice's remaining balance of ${currencySymbol}${formatAmount(
              invoiceBalance
            )}.`
          );
        }
        return prev;
      }

      // Check if amount + TDS exceeds available credits
      // If TDS is already applied, don't count it in the credits calculation
      const otherInvoicesTotal = prev
        .filter(
          (item) => item.isSelected && item.invoice.invoice_id !== invoiceId
        )
        .reduce((sum, item) => {
          const itemAmount = item.amount || 0;
          // If TDS is already applied, don't add TDS to the total
          const itemTds = item.is_tds_applied ? 0 : item.tds_tax_amount || 0;
          return sum + itemAmount + itemTds;
        }, 0);

      const tdsForThisInvoice = isTdsApplied ? 0 : tdsAmount;
      const totalForThisInvoice = roundedAmount + tdsForThisInvoice;
      if (otherInvoicesTotal + totalForThisInvoice > availableCredits + 0.001) {
        const availableCreditsForThis = availableCredits - otherInvoicesTotal;
        if (isTdsApplied) {
          showWarning(
            `Amount (${currencySymbol}${formatAmount(
              roundedAmount
            )}) exceeds available credits. Available: ${currencySymbol}${formatAmount(
              availableCreditsForThis
            )}`
          );
        } else {
          showWarning(
            `Total amount (${currencySymbol}${formatAmount(
              roundedAmount
            )}) + TDS (${currencySymbol}${formatAmount(
              tdsAmount
            )}) exceeds available credits. Available: ${currencySymbol}${formatAmount(
              availableCreditsForThis
            )}`
          );
        }
        return prev;
      }

      const updated = prev.map((item) =>
        item.invoice.invoice_id === invoiceId
          ? { ...item, amount: roundedAmount }
          : item
      );

      setPayInFullUsed(false);
      return updated;
    });
  };

  const handleTdsSelect = (invoiceId: string, tdsRate: TDSRate | null) => {
    setSelectedInvoices((prev) => {
      const updated = prev.map((item) => {
        if (item.invoice.invoice_id === invoiceId) {
          if (!tdsRate) {
            return {
              ...item,
              amount: 0,
              tds_tax_id: undefined,
              tds_tax_percentage: undefined,
              tds_tax_name: undefined,
              tds_section: undefined,
              tds_tax_amount: 0,
            };
          }

          const taxableAmount =
            item.invoice.total - (item.invoice.tax_total || 0);
          const tdsAmount = taxableAmount * (tdsRate.tds_tax_percentage / 100);
          const roundedTdsAmount = Math.round(tdsAmount * 100) / 100;

          return {
            ...item,
            amount: 0,
            tds_tax_id: tdsRate.tds_tax_id,
            tds_section: tdsRate.section,
            tds_tax_percentage: tdsRate.tds_tax_percentage,
            tds_tax_name: tdsRate.tds_tax_name,
            tds_tax_amount: roundedTdsAmount,
          };
        }
        return item;
      });

      setPayInFullUsed(false);
      return updated;
    });
  };

  const handlePayInFull = () => {
    clearErrors();
    const selectedItemsWithoutAmounts = selectedInvoices.filter(
      (item) => item.isSelected && (item.amount === 0 || !item.amount)
    );

    if (selectedItemsWithoutAmounts.length === 0) {
      showWarning(
        "Please select at least one invoice without an amount to pay in full."
      );
      return;
    }

    // Calculate remaining credits (considering TDS for all selected invoices)
    // If TDS is already applied, don't count it in the credits calculation
    const alreadyUsedCredits = selectedInvoices
      .filter((item) => item.isSelected && item.amount > 0)
      .reduce((sum, item) => {
        const amount = item.amount || 0;
        // If TDS is already applied, don't add TDS to the total
        const tds = item.is_tds_applied ? 0 : item.tds_tax_amount || 0;
        return sum + amount + tds;
      }, 0);

    let remainingCreditsForPayInFull = availableCredits - alreadyUsedCredits;
    const invoicesToUpdate: Array<{
      invoiceId: string;
      amount: number;
    }> = [];

    for (const item of selectedItemsWithoutAmounts) {
      if (remainingCreditsForPayInFull <= 0) break;

      const invoiceBalance = item.invoice.balance || 0;
      const tdsAmount = item.tds_tax_amount || 0;
      const isTdsApplied = !!item.is_tds_applied;

      // If TDS is already applied, use full invoice balance and don't subtract TDS
      // Otherwise, subtract TDS from both available credits and invoice balance
      const availableForPayment = isTdsApplied
        ? remainingCreditsForPayInFull
        : remainingCreditsForPayInFull - tdsAmount;

      // Payment amount is min of: available credits, invoice balance
      // If TDS is already applied, use full balance; otherwise subtract TDS
      const maxFromInvoice = isTdsApplied
        ? invoiceBalance
        : invoiceBalance - tdsAmount;

      let paymentAmount = Math.min(
        Math.max(0, availableForPayment),
        maxFromInvoice
      );
      paymentAmount = Math.round(paymentAmount * 100) / 100;

      if (paymentAmount > 0) {
        invoicesToUpdate.push({
          invoiceId: item.invoice.invoice_id,
          amount: paymentAmount,
        });
        // Deduct payment amount and TDS (if not already applied) from remaining credits
        const tdsToDeduct = isTdsApplied ? 0 : tdsAmount;
        remainingCreditsForPayInFull -= paymentAmount + tdsToDeduct;
        remainingCreditsForPayInFull =
          Math.round(remainingCreditsForPayInFull * 100) / 100;
      }
    }

    setSelectedInvoices((prev) => {
      const updated = prev.map((item) => {
        const updateInfo = invoicesToUpdate.find(
          (u) => u.invoiceId === item.invoice.invoice_id
        );
        if (updateInfo) {
          return { ...item, amount: updateInfo.amount };
        }
        return item;
      });
      return updated;
    });

    if (invoicesToUpdate.length > 0) {
      showSuccess("Applied payment amounts to selected invoices.");
    }

    setPayInFullUsed(true);
  };

  const handleApply = async () => {
    setIsApplying(true);
    clearErrors();

    try {
      const selectedInvoicesWithAmounts = selectedInvoices.filter(
        (item) => item.isSelected && item.amount > 0
      );
      const selectedInvoicesWithoutAmounts = selectedInvoices.filter(
        (item) => item.isSelected && item.amount <= 0
      );

      if (selectedInvoicesWithoutAmounts.length > 0) {
        showWarning(
          "Please enter amounts for all selected invoices before applying credits."
        );
        setFieldError(
          "invoices",
          "Please enter amounts for all selected invoices"
        );
        setIsApplying(false);
        return;
      }

      if (selectedInvoicesWithAmounts.length === 0) {
        showWarning("Please select at least one invoice with an amount.");
        setFieldError("invoices", "Please select at least one invoice");
        setIsApplying(false);
        return;
      }

      // Import the API function
      const { applyCreditsToInvoices } = await import(
        "../../data/datasources/RecoApiDataSource"
      );

      const invoices = selectedInvoicesWithAmounts.map((item) => {
        // If TDS is already applied (subsequent match), don't send TDS info
        if (item.invoice.is_tds_applied) {
          return {
            invoice_id: item.invoice.invoice_id,
            invoice_number: item.invoice.invoice_number,
            amount: item.amount,
            balance: item.invoice.balance,
            is_tds_applied: 1,
          };
        }
        // Otherwise, send all TDS information
        return {
          invoice_id: item.invoice.invoice_id,
          invoice_number: item.invoice.invoice_number,
          amount: item.amount,
          balance: item.invoice.balance,
          tds_tax_amount: item.tds_tax_amount,
          tds_tax_id: item.tds_tax_id,
          tds_section: item.tds_section,
          tds_tax_percentage: item.tds_tax_percentage,
          tds_tax_name: item.tds_tax_name,
          is_tds_applied: 0,
        };
      });

      const response = await applyCreditsToInvoices(customerId, invoices);

      if (response.success) {
        showSuccess("Credits applied to invoices successfully!");
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        showError(
          `Failed to apply credits: ${response.message || "Unknown error"}`
        );
        setFieldError("api", response.message || "Failed to apply credits");
      }
    } catch (error: any) {
      console.error("Error applying credits:", error);
      const errorMessage =
        error?.message || "Failed to apply credits. Please try again.";
      showError(errorMessage);
      setApiErrors(error);
    } finally {
      setIsApplying(false);
    }
  };

  const hasValidationErrors = () => {
    const selectedInvoicesWithoutAmounts = selectedInvoices.filter(
      (item) => item.isSelected && item.amount <= 0
    );
    return selectedInvoicesWithoutAmounts.length > 0;
  };

  return (
    <DialogLayout open={open} onOpenChange={handleClose}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Apply Credits to Invoices
        </span>
        {checkHasErrors() && (
          <ErrorBanner
            errors={errors}
            title="Please fix the following errors:"
          />
        )}
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {customerName}
            </span>
            <span className="text-heading-3 font-heading-3 text-success-600">
              {currencySymbol}
              {formatAmount(availableCredits)} Available
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Select Invoices
            </span>
            <div className="flex items-center gap-2">
              <TextField
                className="h-8 w-48"
                label=""
                helpText=""
                icon={<FeatherSearch />}
              >
                <TextField.Input
                  placeholder="Search invoices"
                  value={invoiceSearchTerm}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setInvoiceSearchTerm(event.target.value);
                  }}
                />
              </TextField>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex w-full items-center justify-between">
                <span className="text-body-bold font-body-bold text-default-font">
                  Available Invoices
                  {loadingInvoices && (
                    <span className="text-body font-body text-neutral-500 ml-2">
                      (Loading...)
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-caption font-caption text-neutral-400">
                      Remaining credits:
                    </span>
                    <span
                      className={`text-caption font-caption ${
                        remainingCredits >= 0
                          ? "text-success-600"
                          : "text-error-600"
                      }`}
                    >
                      {currencySymbol}
                      {formatAmount(Math.max(0, remainingCredits))}
                    </span>
                  </div>
                  {selectedInvoices.some((item) => item.isSelected) && (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      onClick={handlePayInFull}
                      disabled={payInFullUsed}
                    >
                      Pay in Full
                    </Button>
                  )}
                </div>
              </div>
              {selectedInvoices.length > 0 ? (
                selectedInvoices.map((item) => (
                  <div
                    key={item.invoice.invoice_id}
                    className={`flex w-full items-center justify-between rounded-md border border-solid px-4 py-3 ${
                      item.isSelected
                        ? item.amount > 0
                          ? "border-success-600 bg-success-100"
                          : "border-warning-600 bg-warning-50"
                        : "border-neutral-border bg-default-background"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <Button
                        className={`h-6 w-6 flex-none ${
                          item.isSelected
                            ? "bg-success-600 text-white"
                            : "border-2 border-dashed border-neutral-300"
                        }`}
                        variant={
                          item.isSelected
                            ? "brand-primary"
                            : "neutral-secondary"
                        }
                        size="small"
                        icon={item.isSelected ? <FeatherCheck /> : undefined}
                        onClick={() =>
                          handleInvoiceSelect(item.invoice.invoice_id)
                        }
                      />
                      <div className="flex flex-col items-start">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {item.invoice.invoice_number}
                        </span>
                        <span className="text-body font-body text-neutral-500">
                          {new Date(item.invoice.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-12">
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Full
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(item.invoice.total)}
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Paid
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(
                            item.invoice.total - item.invoice.balance
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col items-center">
                        <span className="text-caption font-caption text-neutral-400">
                          Remaining
                        </span>
                        <span className="text-body font-body text-default-font">
                          {item.invoice.currency_symbol}
                          {formatAmount(item.invoice.balance)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="flex flex-col items-center gap-1">
                        <TextField
                          className="h-auto w-32 flex-none"
                          label=""
                          helpText=""
                        >
                          <TextField.Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={`Max: ${
                              item.invoice.currency_symbol
                            }${formatAmount(
                              getMaxAmountForInvoice(item.invoice.invoice_id)
                            )}`}
                            value={
                              item.isSelected
                                ? item.amount > 0
                                  ? item.amount.toString()
                                  : ""
                                : ""
                            }
                            disabled={!item.isSelected}
                            onWheel={(e) => {
                              e.currentTarget.blur();
                            }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const inputValue = event.target.value;
                              if (inputValue === "" || inputValue === ".") {
                                handleAmountChange(item.invoice.invoice_id, 0);
                                return;
                              }

                              const decimalRegex = /^\d+(\.\d{0,2})?$/;
                              if (!decimalRegex.test(inputValue)) {
                                return;
                              }

                              const value = parseFloat(inputValue) || 0;
                              const roundedValue =
                                Math.round(value * 100) / 100;
                              handleAmountChange(
                                item.invoice.invoice_id,
                                roundedValue
                              );
                            }}
                          />
                        </TextField>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        {item.is_tds_applied ? (
                          <div className="h-auto w-32 flex-none flex items-center justify-center px-1.5 py-1.5 bg-neutral-100 rounded border border-neutral-300">
                            <span className="text-caption font-caption text-neutral-600 text-center">
                              TDS already applied
                            </span>
                          </div>
                        ) : (
                          <SubframeCore.DropdownMenu.Root>
                            <SubframeCore.DropdownMenu.Trigger asChild={true}>
                              <div className="h-auto w-32 flex-none">
                                <Button
                                  variant="neutral-secondary"
                                  iconRight={<FeatherChevronDown />}
                                  disabled={!item.isSelected}
                                  onClick={(
                                    event: React.MouseEvent<HTMLButtonElement>
                                  ) => {}}
                                >
                                  <span className="text-left truncate text-sm">
                                    {item.tds_tax_name
                                      ? truncateText(
                                          `${item.tds_tax_percentage}% - ${item.tds_tax_name}`,
                                          10
                                        )
                                      : "No TDS"}
                                  </span>
                                </Button>
                              </div>
                            </SubframeCore.DropdownMenu.Trigger>
                            <SubframeCore.DropdownMenu.Portal>
                              <SubframeCore.DropdownMenu.Content
                                side="bottom"
                                align="start"
                                sideOffset={4}
                                asChild={true}
                              >
                                <div className="max-h-48 overflow-y-auto w-64">
                                  <DropdownMenu>
                                    <DropdownMenu.DropdownItem
                                      onClick={() =>
                                        handleTdsSelect(
                                          item.invoice.invoice_id,
                                          null
                                        )
                                      }
                                    >
                                      No TDS
                                    </DropdownMenu.DropdownItem>
                                    {tdsRates.map((rate) => {
                                      const displayText = `${rate.tds_tax_percentage}% - ${rate.tds_tax_name}`;
                                      const truncatedText = truncateText(
                                        displayText,
                                        40
                                      );

                                      return (
                                        <DropdownMenu.DropdownItem
                                          key={rate.tds_tax_id}
                                          onClick={() =>
                                            handleTdsSelect(
                                              item.invoice.invoice_id,
                                              rate
                                            )
                                          }
                                        >
                                          {truncatedText}
                                        </DropdownMenu.DropdownItem>
                                      );
                                    })}
                                  </DropdownMenu>
                                </div>
                              </SubframeCore.DropdownMenu.Content>
                            </SubframeCore.DropdownMenu.Portal>
                          </SubframeCore.DropdownMenu.Root>
                        )}
                        {item.is_tds_applied ? (
                          <span className="text-xs text-neutral-500 text-center w-32 truncate">
                            {item.tds_tax_percentage}% - {item.tds_tax_name}
                          </span>
                        ) : (
                          <span className="text-caption font-caption text-neutral-500 text-center w-32">
                            {item.tds_tax_amount
                              ? `${item.invoice.currency_symbol}${formatAmount(
                                  item.tds_tax_amount
                                )}`
                              : `${item.invoice.currency_symbol}0.00`}
                          </span>
                        )}
                      </div>
                      <Button
                        className={`h-8 w-20 flex-none ${
                          item.isSelected ? "bg-success-600 text-white" : ""
                        }`}
                        size="small"
                        onClick={() =>
                          handleInvoiceSelect(item.invoice.invoice_id)
                        }
                      >
                        {item.isSelected ? "Selected" : "Select"}
                      </Button>
                    </div>
                  </div>
                ))
              ) : !loadingInvoices ? (
                <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
                  <span className="text-body font-body text-neutral-500">
                    No invoices found for {customerName}
                  </span>
                </div>
              ) : null}
              {invoicesHasMore && (
                <div className="flex w-full items-center justify-center pt-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={loadMoreInvoices}
                    disabled={invoicesLoading}
                    loading={invoicesLoading}
                  >
                    {invoicesLoading ? "Loading..." : "Load More Invoices"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-6">
          <div className="flex items-center gap-2">
            <Button variant="neutral-tertiary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={hasValidationErrors() || isApplying}
              loading={isApplying}
              className={
                hasValidationErrors() ? "opacity-50 cursor-not-allowed" : ""
              }
            >
              Apply Credits
            </Button>
          </div>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ApplyCreditsModal;
